import React, { useMemo, useRef, useState } from 'react';

import {
  Box,
  ChevronBackButton,
  Col,
  Image,
  PressableBox,
  Row,
  ShareButton,
  Text,
} from '@opendoor/bricks-next';
import { buildFloorPlanSrc } from 'components/marketplace/helpers/galleryPhotos';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import { Map } from 'components/marketplace/property';
import { DynamicComponentWithCondition } from 'components/shared/dynamic';
import { useInViewAfterLoad } from 'components/shared/hooks/useInView';
import { formatCurrency } from 'helpers/formatters';

import type { InteractiveGoogleStreetViewProps } from '../../../properties/components/InteractiveGoogleStreetView';
import Modal from './Modal';
import { ModalContent } from './PhotoCarousel';

interface MobileModalContentProps {
  address?: string | null;
  closeModal: () => void;
  contentToShow: ModalContent;
  isModalOpen: boolean;
  latitude?: number | null;
  longitude?: number | null;
  openLightbox: (idx: number) => void;
  photos: GalleryPhoto[] | null | undefined;
  price?: number | null;
  openShareModal: () => void;
}

enum MapContent {
  MAP = 'Map',
  STREETVIEW = 'Street view',
}

export const MobileModalContent: React.FC<MobileModalContentProps> = React.memo(
  ({
    address,
    closeModal,
    contentToShow,
    isModalOpen,
    latitude,
    longitude,
    openLightbox,
    photos,
    price,
    openShareModal,
  }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [content, setContent] = useState<MapContent>(MapContent.MAP);
    const { inView: inViewMapPage } = useInViewAfterLoad({
      triggerOnce: true,
      initialInView: true,
      ref,
    });

    const mappedPhotos = useMemo(
      () =>
        photos?.map((photo) => {
          const url = photo.isFloorPlan
            ? buildFloorPlanSrc(photo.url)
            : photo.url + '?service=cosmos&preset=3%3A2-md&presetScale=2%2F3&dpr=1';
          const alt = photo.isFloorPlan ? photo.label?.trim() || 'Floor plan' : '';
          return { url, alt, isFloorPlan: photo.isFloorPlan };
        }) || [],
      [photos],
    );

    const switchToMap = () => setContent(MapContent.MAP);
    const switchToStreetView = () => setContent(MapContent.STREETVIEW);

    return (
      <Modal
        overflow={contentToShow === ModalContent.MAP ? 'hidden' : 'auto'}
        isOpen={isModalOpen}
        analyticsName="marketplace-mobile-photo-gallery-modal"
        onRequestClose={closeModal}
        headerProps={{
          rightComponent: (
            <ShareButton
              analyticsName="marketplace-mobile-photo-gallery-share-button"
              size="medium"
              onPress={openShareModal}
            />
          ),
          title: price ? formatCurrency(price, { inDollars: true }) : 'Price not available',
          titleProps: {
            typography: '$labelMedium',
            alignSelf: 'center',
            pt: '$3',
          },
          subtitleProps: {
            typography: '$bodyXsmall',
            alignSelf: 'center',
            color: '$contentSecondary',
          },
          subtitle: address || 'Address not available',
          leftComponent: (
            <ChevronBackButton
              analyticsName="marketplace-mobile-photo-gallery-back-button"
              size="medium"
              onPress={closeModal}
              data-testid="marketplace-mobile-photo-gallery-back-button"
            />
          ),
        }}
      >
        <Col flex={1} data-testid="mobile-image-gallery">
          {contentToShow === ModalContent.PHOTOS &&
            mappedPhotos.map((photo, idx) => (
              <PressableBox
                key={`${idx}-photo`}
                mb="$1half"
                onPress={() => openLightbox(idx)}
                analyticsName={
                  photo.isFloorPlan
                    ? 'marketplace-mobile-photo-gallery-floor-plan-clicked'
                    : 'marketplace-mobile-photo-gallery-photo-clicked'
                }
                aria-label={photo.alt || `image gallery photo ${idx + 1}`}
              >
                {photo.isFloorPlan ? (
                  <Box
                    width="100%"
                    aspectRatio={3 / 2}
                    backgroundColor="$backgroundSecondary"
                    overflow="hidden"
                  >
                    <Image
                      alt={photo.alt || 'Floor plan'}
                      source={{ uri: photo.url }}
                      resizeMode="contain"
                      width="100%"
                      height="100%"
                    />
                  </Box>
                ) : (
                  <Image
                    alt={photo.alt || 'property photo'}
                    source={{ uri: photo.url }}
                    width="100%"
                    height="auto"
                    aspectRatio={3 / 2}
                  />
                )}
              </PressableBox>
            ))}
          {contentToShow === ModalContent.MAP && latitude && longitude && (
            <Col flex={1}>
              <Row
                backgroundColor="$backgroundPrimary"
                justifyContent="center"
                height="$13"
                alignItems="center"
                width="100%"
                flexShrink={0}
              >
                <PressableBox
                  analyticsName="marketplace-mobile-modal-map-button"
                  aria-label="Map"
                  flexDirection="row"
                  justifyContent="center"
                  px="$5"
                  width="50%"
                  onPress={switchToMap}
                  height="100%"
                >
                  <Text
                    tag="span"
                    typography="$labelMedium"
                    color={content === MapContent.MAP ? '$contentPrimary' : '$contentTertiary'}
                    fontWeight={content === MapContent.MAP ? '$medium' : '$regular'}
                  >
                    Map
                  </Text>
                </PressableBox>
                <PressableBox
                  analyticsName="marketplace-mobile-modal-street-view-button"
                  aria-label="Street view"
                  flexDirection="row"
                  justifyContent="center"
                  px="$5"
                  width="50%"
                  onPress={switchToStreetView}
                  height="100%"
                >
                  <Text
                    tag="span"
                    typography="$labelMedium"
                    color={
                      content === MapContent.STREETVIEW ? '$contentPrimary' : '$contentTertiary'
                    }
                    fontWeight={content === MapContent.STREETVIEW ? '$medium' : '$regular'}
                  >
                    Street view
                  </Text>
                </PressableBox>
              </Row>
              <Col flex={1}>
                {content === MapContent.MAP && (
                  <Map key="property-location" latitude={latitude} longitude={longitude} />
                )}
                {content === MapContent.STREETVIEW && (
                  <Col height="100%">
                    <DynamicComponentWithCondition<InteractiveGoogleStreetViewProps>
                      dynamicLoaderFunction={() =>
                        import('../../../properties/components/InteractiveGoogleStreetView').then(
                          (mod) => mod.InteractiveGoogleStreetView,
                        )
                      }
                      loadingCondition={inViewMapPage}
                      latitude={latitude}
                      longitude={longitude}
                      addressControl
                      linksControl
                      zoomControl
                    />
                  </Col>
                )}
              </Col>
            </Col>
          )}
        </Col>
      </Modal>
    );
  },
);

MobileModalContent.displayName = 'MobileModalContent';
